import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { subscribeN4, when } from '../lib/n4';
import { Decode } from '../lib/fx';

interface Project {
  id: number;
  name: string;
  dir: string;
  description: string;
  agent_name?: string;
  created_at: number;
}
interface Stats { versions: number; workers: number; lastVersion: { number: number; message: string; created_at: number } | null; live: unknown[]; story: { text: string; created_at: number }[] }
interface Usage { project_id: number; output_tokens: number; last_at: number | null }

export function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [agentName, setAgentName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState<Record<number, Stats>>({});
  const [usage, setUsage] = useState<Record<number, Usage>>({});
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [menu, setMenu] = useState<number | null>(null);
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [dirPath, setDirPath] = useState('');
  const [browse, setBrowse] = useState<{ path: string; parent: string | null; dirs: { name: string; path: string }[] } | null>(null);
  const openBrowser = (at = '') => api<{ path: string; parent: string | null; dirs: { name: string; path: string }[] }>(`/api/fs/dirs?path=${encodeURIComponent(at)}`).then(setBrowse).catch(() => {});

  const load = () =>
    api<{ projects: Project[] }>('/api/projects')
      .then((r) => { setProjects(r.projects); for (const pr of r.projects) api<Stats>(`/api/n4/projects/${pr.id}/overview`).then((o) => setStats((st) => ({ ...st, [pr.id]: o }))).catch(() => {}); })
      .catch(() => setProjects([]));
  const loadUsage = () => api<{ projects: Usage[] }>('/api/stats').then((r) => setUsage(Object.fromEntries(r.projects.map((u) => [u.project_id, u])))).catch(() => {});

  useEffect(() => {
    void load(); void loadUsage();
    // A futó ügynökök és az utolsó események frissen tartása.
    const t = window.setInterval(() => void load(), 30_000);
    // Ablak indult / kilépett: a kártyák lámpája azonnal frissül.
    const off = subscribeN4((ev) => { if (ev.type === 'session') void load(); });
    return () => { clearInterval(t); off(); };
  }, []);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/projects', { method: 'POST', body: JSON.stringify(mode === 'existing' ? { path: dirPath, description, agent_name: agentName } : { name, description, agent_name: agentName }) });
      setName(''); setDirPath(''); setBrowse(null);
      setDescription(''); setAgentName(''); setShowNew(false);
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'name_taken') setError('Ilyen nevű projekted már van.');
      else if (err instanceof ApiError && err.code === 'dir_taken') setError('Ez a mappa már projekt.');
      else if (err instanceof ApiError && err.code === 'invalid_name' && err.detail) setError(err.detail);
      else if (err instanceof ApiError && err.code === 'invalid_name')
        setError('Érvénytelen mappanév — csak betű/szám/pont/kötőjel/alsóvonás, max 64 karakter.');
      else setError('Nem sikerült létrehozni a projektet.');
    } finally {
      setBusy(false);
    }
  };

  // Törlés mindenestül: a megerősítés mutatja a mappát (és ha git-tár van benne, vagy él az ablak), az eredmény visszajelzés
  const remove = async (id: number) => {
    const p = projects.find((x) => x.id === id);
    let pre: { dir: string; dir_exists: boolean; dir_blocker: string | null; has_git: boolean; live: number } | null = null;
    try { pre = await api(`/api/projects/${id}/purge-preview`); } catch { /* a megerősítés enélkül is megy */ }
    const dir = pre?.dir ?? p?.dir ?? '';
    const lines = [`Törlöd a(z) „${p?.name ?? id}” projektet MINDENESTÜL?`, '',
      'Törlődik az APM-ből: verziók, történet, Tervező, képek, munkások, toolok, szkriptek, tanulságok, napló, fogyasztás.',
      !pre ? `Az előnézet nem elérhető (az APM még a régi szerverrel fut?) — ilyenkor csak a listáról kerül le, a mappa (${dir}) és az adatok megmaradnak.`
        : pre.dir_blocker ? `A mappa NEM törlődik (${pre.dir_blocker}): ${dir}` : `A MAPPA IS TÖRLŐDIK A LEMEZRŐL: ${dir}${!pre.dir_exists ? ' (már nincs meg)' : ''}`,
      pre?.has_git ? 'FIGYELEM: a mappában git-tár (.git) van — az is törlődik!' : '',
      pre?.live ? `${pre.live} élő ablak leáll.` : '', '', 'Nem visszavonható.'].filter((l, i, a) => l !== '' || a[i - 1] !== '');
    if (!confirm(lines.join('\n'))) return;
    try {
      const r = await api<{ ok: boolean; dir_removed: boolean; warnings: string[]; blobs?: { files: number } }>(`/api/projects/${id}`, { method: 'DELETE' });
      if (r.warnings?.length) alert(`A projekt törölve, de nem minden sikerült:\n${r.warnings.join('\n')}`);
    } catch { alert('Nem sikerült törölni a projektet.'); }
    await load();
  };

  const inputClass = 'a-input';

  // Utolsó aktivitás: a legfrissebb a fogyasztás, a verzió és a történet időpontjai közül.
  const lastOf = (p: Project) => Math.max(usage[p.id]?.last_at ?? 0, stats[p.id]?.lastVersion?.created_at ?? 0, stats[p.id]?.story?.[0]?.created_at ?? 0, p.created_at);
  const isLive = (p: Project) => Boolean(stats[p.id]?.live?.length);
  const fmtTok = (n?: number) => (!n ? '—' : n >= 1e6 ? `${(n / 1e6).toFixed(1).replace('.', ',')} M` : `${Math.round(n / 1000)} k`);
  const needle = q.trim().toLowerCase();
  const shown = projects
    .filter((p) => !needle || `${p.name} ${p.description} ${p.dir} ${stats[p.id]?.story?.[0]?.text ?? ''}`.toLowerCase().includes(needle))
    .sort((a, b) => Number(isLive(b)) - Number(isLive(a)) || lastOf(b) - lastOf(a));
  const liveCount = projects.filter(isLive).length;
  const QUICK: [string, string][] = [['repo', 'Repo'], ['workflow', 'Workflow'], ['tervezo', 'Tervező'], ['kepek', 'Képek'], ['tortenet', 'Történet']];

  return (
    <div className="h-full overflow-auto" onClick={() => setMenu(null)}>
     <div className="a-pg">
      <header className="a-pgh">
        <div>
          <h1 className="a-title"><Decode text="Projektek" duration={800} /></h1>
          <p className="a-sub">{projects.length} projekt{liveCount ? `, ebből ${liveCount}-ban/ben dolgozik most ügynök` : ''}.</p>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <input className="a-input sm:!w-64" placeholder="Keresés név, mappa vagy történet szerint" value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="a-btn solid" onClick={() => { setShowNew(true); setError(null); }}>+ Új projekt</button>
        </div>
      </header>

      <div className="a-pgrid a-stagger">
        {shown.map((p, idx) => {
          const st = stats[p.id]; const live = isLive(p); const last = lastOf(p); const story = st?.story?.[0]?.text;
          return (
            <div key={p.id} role="button" tabIndex={0} onClick={() => navigate(`/projects/${p.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/projects/${p.id}`); }}
              title="Megnyitás" className={`a-pcard ${live ? 'working' : ''}`} style={{ ['--i' as string]: idx }}>
              <div className="pc-top"><i className={`a-lamp ${live ? 'on' : ''}`} /><span className="pc-dir" title={p.dir}>{p.dir.replace(/^\/home\/[^/]+/, '~')}</span>
                <span role="button" tabIndex={0} title="Továbbiak" className="pc-more" onClick={(e) => { e.stopPropagation(); setMenu(menu === p.id ? null : p.id); }}>⋯</span></div>
              {menu === p.id && (
                <div className="a-menu" style={{ position: 'absolute', right: 8, top: 34, zIndex: 20 }} onClick={(e) => e.stopPropagation()}>
                  <div role="button" tabIndex={0} onClick={() => { setMenu(null); navigate(`/projects/${p.id}?tab=repo`); }}>Repo megnyitása</div>
                  <div role="button" tabIndex={0} className="danger" onClick={() => { setMenu(null); void remove(p.id); }} title="Minden adat és a mappa is törlődik">Törlés mindenestül…</div>
                </div>
              )}
              <h3 className="pc-name">{p.name}</h3>
              {p.agent_name && <p className="pc-agent" title="Az ügynök neve">ügynök: <b>{p.agent_name}</b></p>}
              {live && <span className="pc-live">ügynök dolgozik</span>}
              {p.description && <p className="pc-desc">{p.description}</p>}
              {story && <p className="pc-story">{story}</p>}
              <div className="pc-foot">
                <span><b>{st?.versions ?? 0}</b> verzió</span>
                <span>utoljára <b>{when(last)}</b></span>
                <span><b>{fmtTok(usage[p.id]?.output_tokens)}</b> token</span>
              </div>
              <div className="pc-quick">
                {QUICK.map(([tab, label]) => (
                  <span key={tab} role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); navigate(`/projects/${p.id}?tab=${tab}`); }}>{label}</span>
                ))}
              </div>
            </div>
          );
        })}
        {!needle && (
          <div role="button" tabIndex={0} onClick={() => { setShowNew(true); setError(null); }} className="a-pcard newtile" style={{ ['--i' as string]: shown.length }}>
            <span className="plus">+</span>
            <span className="nt">Új projekt</span>
            <span className="a-sub" style={{ fontSize: 12 }}>új mappa vagy meglévő hozzáadása</span>
          </div>
        )}
        {needle && shown.length === 0 && <p className="a-sub">Nincs találat erre: „{q}”.</p>}
      </div>
     </div>

      {showNew && (
        <div className="a-overlay fixed inset-0 z-40 flex items-start justify-center overflow-auto p-4 pt-16" onClick={() => setShowNew(false)}>
          <div className="a-panel a-pad a-modal-in w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-[20px] text-neutral-100">Új projekt</h3>
              <span role="button" tabIndex={0} className="cursor-pointer px-2 text-neutral-500 hover:text-neutral-200" onClick={() => setShowNew(false)}>✕</span>
            </div>
            <form
              onSubmit={create}
              className="space-y-3"
            >
              <div className="flex gap-2 text-xs">
                {(['new', 'existing'] as const).map((m) => (
                  <button key={m} type="button" onClick={() => { setMode(m); setError(null); if (m === 'existing' && !browse) void openBrowser(); }} className={`rounded-lg px-3 py-1.5 transition ${mode === m ? 'bg-white/10 text-neutral-100' : 'text-neutral-500 hover:text-neutral-300'}`}>
                    {m === 'new' ? 'Új mappa' : 'Meglévő mappa'}
                  </button>
                ))}
              </div>
              {mode === 'new' ? (
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-500">Mappanév</label>
                  <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="pl. weboldal vagy Desktop/jatek" />
                  <p className="mt-1 text-[11px] text-neutral-600">A munkaterületeden belül jön létre (almappa is lehet).</p>
                </div>
              ) : (
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-500">Meglévő mappa útvonala</label>
                  <input className={inputClass} value={dirPath} onChange={(e) => setDirPath(e.target.value)} placeholder="~/Desktop/projektem" />
                  <p className="mt-1 text-[11px] text-neutral-600">Csak a home-mappád alatti mappa lehet. A projekt neve a mappa neve lesz. Vagy válaszd ki alább:</p>
                  {browse && (
                    <div className="mt-2 rounded-lg border border-[var(--color-term-border)] bg-black/30 p-2 text-xs">
                      <div className="mb-1 flex items-center gap-2 font-mono text-neutral-400">
                        <span className="truncate">{browse.path}</span>
                        <span className="flex-1" />
                        {browse.parent && <button type="button" className="text-neutral-300 hover:text-neutral-100" onClick={() => void openBrowser(browse.parent ?? '')}>↑ feljebb</button>}
                        <button type="button" className="rounded border border-[var(--color-accent)]/50 px-2 py-0.5 text-[var(--color-accent)]" onClick={() => setDirPath(browse.path)}>Ezt választom</button>
                      </div>
                      <div className="max-h-48 overflow-auto">
                        {browse.dirs.map((d) => (
                          <div key={d.path} className="flex items-center gap-2 rounded px-2 py-1 hover:bg-white/5">
                            <button type="button" className="min-w-0 flex-1 truncate text-left text-neutral-200" onClick={() => void openBrowser(d.path)}>📁 {d.name}</button>
                            <button type="button" className="text-neutral-500 hover:text-[var(--color-accent)]" onClick={() => setDirPath(d.path)}>kiválaszt</button>
                          </div>
                        ))}
                        {browse.dirs.length === 0 && <div className="px-2 py-1 text-neutral-600">nincs almappa</div>}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-500">Az ügynök neve (opcionális)</label>
                <input className={inputClass} value={agentName} maxLength={40} onChange={(e) => setAgentName(e.target.value)} placeholder="pl. Vili" />
                <p className="mt-1 text-[11px] text-neutral-600">A projekt fő ügynökét így hívod te és a többi ügynök (a chatben is). Később a Szabályok fülön módosítható; üresen a projekt nevén szólítják.</p>
              </div>
              <div>
                <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-500">Leírás (opcionális)</label>
                <input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
              {error && <p className="text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                disabled={busy || (mode === 'new' ? !name : !dirPath)}
                className="a-btn solid"
              >
                {busy ? 'Mentés…' : mode === 'new' ? 'Projekt létrehozása' : 'Mappa hozzáadása'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
